import React from 'react';
import type { ArticleFigure as Figure } from '../../data/insights';
interface ArticleFigureProps {
  figure: Figure;
}

/**
 * The single chart or diagram that accompanies each article. Drawn in the site
 * palette rather than embedded as an image, so it stays sharp and readable.
 */
export function ArticleFigure({
  figure
}: ArticleFigureProps) {
  return <figure className="mt-12 rounded-md border border-line bg-mist p-6 sm:p-8" data-id="element-739" data-mp="l1ushd2y05vz">
      <p className="font-display text-[19px] leading-snug text-ink" data-id="element-740" data-mp="h3negpzv5ioh">{figure.title}</p>
      <div className="mt-6" data-id="element-741" data-mp="c2g2sfulnzcj">
        {figure.kind === 'line' && <LineChart figure={figure} data-id="element-742" data-mp="73wg2d86mjj4" />}
        {figure.kind === 'bars' && <BarChart figure={figure} data-id="element-743" data-mp="768yerhuqvwp" />}
        {figure.kind === 'flow' && <FlowDiagram figure={figure} data-id="element-744" data-mp="264r9s2u3dj5" />}
      </div>
      {figure.note && <p className="mt-5 text-small text-muted" data-id="element-745" data-mp="21hxewy3qf6j7">{figure.note}</p>}
      <figcaption className="mt-4 border-t border-line pt-4 text-[14px] italic text-body" data-id="element-746" data-mp="21g35afxwv0uf">{figure.caption}</figcaption>
    </figure>;
}
function LineChart({
  figure
}: {
  figure: Extract<Figure, {
    kind: 'line';
  }>;
}) {
  const width = 640;
  const height = 300;
  const pad = {
    top: 16,
    right: 20,
    bottom: 44,
    left: 56
  };
  const values = figure.points.map(p => p.y);
  const min = figure.yMin ?? Math.min(...values);
  const max = figure.yMax ?? Math.max(...values);
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const xAt = (i: number) => pad.left + innerW * i / Math.max(figure.points.length - 1, 1);
  const yAt = (v: number) => pad.top + innerH - (v - min) / (max - min) * innerH;
  const ticks = Array.from({
    length: 5
  }, (_, i) => min + (max - min) * i / 4);
  const path = figure.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i)},${yAt(p.y)}`).join(' ');
  return <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img" aria-label={`${figure.title}. ${figure.points.map(p => `${p.x}: ${p.label ?? p.y}`).join(', ')}`} data-id="element-747" data-mp="3lphsj67ehndy">
      {ticks.map(t => <g key={t} data-id="element-748" data-mp="45rctsj7ih5y">
          <line x1={pad.left} x2={width - pad.right} y1={yAt(t)} y2={yAt(t)} stroke="#1E4374" strokeWidth={1} data-id="element-749" data-mp="46189vn0tgj1" />
          <text x={pad.left - 10} y={yAt(t) + 4} textAnchor="end" fontSize={12} fill="#8FA3BF" data-id="element-750" data-mp="3vb0xd4ofm386">
            {Number.isInteger(t) ? t : t.toFixed(1)}
          </text>
        </g>)}
      <path d={path} fill="none" stroke="#FF5C1A" strokeWidth={2.5} strokeLinejoin="round" data-id="element-751" data-mp="3mc5spx11d16z" />
      {figure.points.map((p, i) => <g key={p.x} data-id="element-752" data-mp="3cmm8oo1l71aj" data-mpi={i}>
          <circle cx={xAt(i)} cy={yAt(p.y)} r={p.reported ? 5.5 : 4} fill={p.reported ? '#FF5C1A' : '#0A2544'} stroke="#FF5C1A" strokeWidth={2} data-id="element-753" data-mp="3cm43h1wy5cqh" />
          {p.label && <text x={xAt(i)} y={yAt(p.y) - 12} textAnchor="middle" fontSize={12} fontWeight={600} fill="#F2F6FB" data-id="element-754" data-mp="33juuwhl8ntfq">
              {p.label}
            </text>}
          <text x={xAt(i)} y={height - pad.bottom + 20} textAnchor="middle" fontSize={12} fill="#8FA3BF" data-id="element-755" data-mp="2tp2wjbju12jw">
            {p.x}
          </text>
        </g>)}
      <text x={pad.left + innerW / 2} y={height - 6} textAnchor="middle" fontSize={12} fill="#8FA3BF" data-id="element-756" data-mp="1e4k4iar5qulc">
        {figure.xLabel}
      </text>
      <text x={14} y={pad.top + innerH / 2} textAnchor="middle" fontSize={12} fill="#8FA3BF" transform={`rotate(-90 14 ${pad.top + innerH / 2})`} data-id="element-757" data-mp="1j5877tor0zve">
        {figure.yLabel}
      </text>
    </svg>;
}
function BarChart({
  figure
}: {
  figure: Extract<Figure, {
    kind: 'bars';
  }>;
}) {
  const max = figure.max ?? Math.max(...figure.bars.map(b => b.value));
  return <dl className="space-y-4" data-id="element-758" data-mp="zemncernb55n">
      {figure.bars.map(bar => <div key={bar.label} className="grid gap-2 sm:grid-cols-[minmax(0,15rem)_1fr] sm:items-center sm:gap-5" data-id="element-759" data-mp="147o64xuz3oui">
          <dt className="text-[14px] text-body" data-id="element-760" data-mp="7nypbbl9qfw7">{bar.label}</dt>
          <dd className="flex items-center gap-3" data-id="element-761" data-mp="bptnw8btef6a">
            <span className="h-3 flex-1 overflow-hidden rounded-full bg-charcoal" data-id="element-762" data-mp="boimgf5udjic">
              <span className="block h-full rounded-full bg-gold" style={{
            width: `${bar.value / max * 100}%`
          }} data-id="element-763" data-mp="3orw1p1potyq1" />
            </span>
            <span className="w-24 shrink-0 text-right font-display text-[16px] text-ink" data-id="element-764" data-mp="3jq6s7qi45t42">{bar.display ?? `${bar.value}%`}</span>
          </dd>
        </div>)}
    </dl>;
}
function FlowDiagram({
  figure
}: {
  figure: Extract<Figure, {
    kind: 'flow';
  }>;
}) {
  return <ol className="grid gap-3 sm:grid-cols-5" data-id="element-765" data-mp="ban230cqoqt8">
      {figure.steps.map((step, index) => <li key={step.label} className="relative flex flex-col rounded-sm border border-line bg-charcoal p-4" data-id="element-766" data-mp="6cljshn50vit" data-mpi={index}>
          <span className="font-display text-[13px] text-gold" data-id="element-767" data-mp="l9mbil5bwqha">{index + 1}</span>
          <span className="mt-2 text-[15px] font-medium text-ink" data-id="element-768" data-mp="gcm5ubce5pqp">{step.label}</span>
          <span className="mt-1 text-[13px] text-muted" data-id="element-769" data-mp="2n2sdbxs3y485">{step.verb}</span>
        </li>)}
    </ol>;
}